/**
 * Enhanced API Service with caching, batching, and error handling
 * Optimized for the new robust backend architecture
 */

import axios from 'axios';

class ApiService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes to match backend
    this.requestQueue = new Map();
    this.batchTimeout = 100; // 100ms batching window
    this.csrfToken = null; // Store CSRF token

    // Setup axios defaults
    this.setupAxiosInterceptors();
  }

  /**
   * Set CSRF token for all subsequent requests
   */
  setCsrfToken(token) {
    this.csrfToken = token;
    console.log('🔒 CSRF token updated in ApiService');
  }

  /**
   * Get current CSRF token
   */
  getCsrfToken() {
    return this.csrfToken;
  }

  setupAxiosInterceptors() {
    // Request interceptor for auth headers and CSRF token
    axios.interceptors.request.use((config) => {
      // Add JWT token
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add CSRF token for state-changing methods
      const method = config.method?.toUpperCase();
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && this.csrfToken) {
        config.headers['X-CSRF-Token'] = this.csrfToken;
      }

      // Always include credentials for cookies
      config.withCredentials = true;

      return config;
    });

    // Response interceptor for error handling
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
          fallback: error.response?.data?.fallback || false
        });

        // Handle CSRF token errors
        if (error.response?.status === 403 &&
            error.response?.data?.message?.includes('CSRF')) {
          console.error('🚨 CSRF validation failed - token may be expired');
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Generate cache key from request config
   */
  generateCacheKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${url}?${new URLSearchParams(sortedParams).toString()}`;
  }

  /**
   * Get cached data if available and not expired
   */
  getFromCache(cacheKey) {
    const cached = this.cache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      console.log(`🚀 Cache hit for: ${cacheKey}`);
      return cached.data;
    }
    
    return null;
  }

  /**
   * Store data in cache
   */
  setCache(cacheKey, data) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Cleanup old entries periodically
    if (this.cache.size > 100) {
      this.cleanupCache();
    }
  }

  /**
   * Cleanup expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  invalidateCache(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        console.log(`🗑️ Cache invalidated for: ${key}`);
      }
    }
  }

  /**
   * Enhanced GET with caching and deduplication
   */
  async get(url, params = {}, options = {}) {
    const { cache = true, dedupe = true } = options;
    const cacheKey = this.generateCacheKey(url, params);
    
    // Check cache first
    if (cache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return { data: cached, fromCache: true };
      }
    }
    
    // Check if request is already in flight (deduplication)
    if (dedupe && this.requestQueue.has(cacheKey)) {
      console.log(`⏳ Deduplicating request for: ${cacheKey}`);
      return this.requestQueue.get(cacheKey);
    }
    
    // Make the request
    const requestPromise = axios.get(url, { params })
      .then(response => {
        // Cache successful responses
        if (cache && response.status === 200) {
          this.setCache(cacheKey, response.data);
        }
        
        // Remove from queue
        this.requestQueue.delete(cacheKey);
        
        return { 
          data: response.data, 
          fromCache: false,
          aggregated: response.data.aggregated || false,
          cached: response.data.cached || false
        };
      })
      .catch(error => {
        this.requestQueue.delete(cacheKey);
        throw error;
      });
    
    // Add to queue for deduplication
    if (dedupe) {
      this.requestQueue.set(cacheKey, requestPromise);
    }
    
    return requestPromise;
  }

  /**
   * POST with cache invalidation
   */
  async post(url, data, options = {}) {
    const response = await axios.post(url, data);
    
    // Invalidate related cache entries
    this.invalidateAfterMutation(url, 'POST');
    
    return { data: response.data, fromCache: false };
  }

  /**
   * PUT with cache invalidation
   */
  async put(url, data, options = {}) {
    const response = await axios.put(url, data);
    
    // Invalidate related cache entries
    this.invalidateAfterMutation(url, 'PUT');
    
    return { data: response.data, fromCache: false };
  }

  /**
   * DELETE with cache invalidation
   */
  async delete(url, options = {}) {
    const response = await axios.delete(url);
    
    // Invalidate related cache entries
    this.invalidateAfterMutation(url, 'DELETE');
    
    return { data: response.data, fromCache: false };
  }

  /**
   * Invalidate cache after mutations
   */
  invalidateAfterMutation(url, method) {
    // Determine what cache entries to invalidate based on the URL
    if (url.includes('/reservations')) {
      this.invalidateCache('/api/reservations');
      this.invalidateCache('/api/reservations/integration/all');
      this.invalidateCache('/api/reservations/integration/stats/overview');
    }
    
    if (url.includes('/inventory')) {
      this.invalidateCache('/api/inventory');
    }
    
    if (url.includes('/customers')) {
      this.invalidateCache('/api/customers');
    }
    
    console.log(`🔄 Cache invalidated after ${method} to ${url}`);
  }

  /**
   * Batch multiple requests together
   */
  async batchRequests(requests) {
    console.log(`📦 Batching ${requests.length} requests`);
    
    try {
      const responses = await Promise.allSettled(
        requests.map(({ method = 'GET', url, params, data }) => {
          switch (method.toUpperCase()) {
            case 'POST':
              return this.post(url, data);
            case 'PUT':
              return this.put(url, data);
            case 'DELETE':
              return this.delete(url);
            default:
              return this.get(url, params);
          }
        })
      );
      
      return responses.map((response, index) => ({
        request: requests[index],
        success: response.status === 'fulfilled',
        data: response.status === 'fulfilled' ? response.value.data : null,
        error: response.status === 'rejected' ? response.reason : null,
        fromCache: response.status === 'fulfilled' ? response.value.fromCache : false
      }));
    } catch (error) {
      console.error('Batch request error:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data with intelligent batching
   */
  async getDashboardData() {
    const requests = [
      { url: '/api/inventory/items/stats/overview' },
      { url: '/api/reservations/integration/stats/overview' },
      { url: '/api/customers/stats/overview' },
      { url: '/api/payments/stats/overview' },
      { url: '/api/reservations/integration/all', params: { status: 'ACTIVE', limit: 10 } }
    ];
    
    const responses = await this.batchRequests(requests);
    
    return {
      inventory: responses[0].data,
      reservations: responses[1].data,
      customers: responses[2].data,
      payments: responses[3].data,
      activeReservations: responses[4].data,
      fromCache: responses.some(r => r.fromCache),
      aggregated: responses.some(r => r.data?.aggregated)
    };
  }

  /**
   * Get reservations with enhanced filtering
   */
  async getReservations(filters = {}) {
    return this.get('/api/reservations/integration/all', filters, { cache: true });
  }

  /**
   * Get reservation details with caching
   */
  async getReservationDetails(id) {
    return this.get(`/api/reservations/integration/${id}`, {}, { cache: true });
  }

  /**
   * Create reservation with optimistic updates
   */
  async createReservation(data) {
    const response = await this.post('/api/reservations/items/create', data);
    
    // Optimistically add to local cache
    // This would be more complex in a real implementation
    
    return response;
  }

  /**
   * Update reservation status
   */
  async updateReservationStatus(id, action, data = {}) {
    const endpoint = `/api/reservations/processing/${id}/${action}`;
    return this.post(endpoint, data);
  }

  /**
   * Clear all cache (useful for logout or manual refresh)
   */
  clearCache() {
    this.cache.clear();
    console.log('🗑️ All cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    const now = Date.now();
    let expired = 0;
    let active = 0;
    
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        expired++;
      } else {
        active++;
      }
    }
    
    return {
      total: this.cache.size,
      active,
      expired,
      hitRatio: this.hitRatio || 0
    };
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;